import React, { useEffect, useMemo, useState } from 'react';
import { Heading } from '../layout';
import { apiClient, API_BASE_URL } from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';

const MONTH_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const formatMoney = (value) =>
  `$ ${Number(value || 0).toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;

const parseEntryDate = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const monthKeyFromDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const lastNMonthKeys = (n) => {
  const out = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(monthKeyFromDate(d));
  }
  return out;
};

const monthLabel = (key) => {
  const [y, m] = String(key).split('-');
  const mi = Number(m) - 1;
  if (!y || mi < 0 || mi > 11) return key;
  return `${MONTH_SHORT[mi]} ${y.slice(2)}`;
};

const EXPENSE_PALETTE = ['#f87171', '#fb923c', '#facc15', '#4ade80', '#60a5fa', '#a78bfa', '#f472b6'];

const kpiColor = (tone) => {
  if (tone === 'negative') return '#fb7185';
  if (tone === 'info') return '#60a5fa';
  return '#34d399';
};

const BackOfficeDashboardWorkspace = ({ isDarkMode = false }) => {
  const { hasPermission } = useAuth();
  const canReadFinance = hasPermission('finance.read');
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const colors = isDarkMode
    ? {
        panel: '#0b1530',
        panelBorder: '#243457',
        title: '#f8fafc',
        text: '#cbd5e1',
        muted: '#7c8aa5',
        headerMuted: '#64748b',
      }
    : {
        panel: '#ffffff',
        panelBorder: '#e2e8f0',
        title: '#0f172a',
        text: '#334155',
        muted: '#64748b',
        headerMuted: '#64748b',
      };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      if (!canReadFinance) {
        if (!cancelled) {
          setSummary(null);
          setEntries([]);
          setLoading(false);
        }
        return;
      }
      try {
        const [sumJson, entJson] = await Promise.all([
          apiClient(`${API_BASE_URL}/api/finance/summary`),
          apiClient(`${API_BASE_URL}/api/finance/entries`),
        ]);
        if (!cancelled) {
          setSummary(sumJson && typeof sumJson === 'object' ? sumJson : null);
          setEntries(Array.isArray(entJson) ? entJson : []);
        }
      } catch (e) {
        if (!cancelled) {
          setSummary(null);
          setEntries([]);
          const msg = e?.message || 'No se pudo cargar el dashboard.';
          if (
            /403|422|permiso|forbidden|HTML_INSTEAD_OF_JSON|HTML en lugar de JSON|Unexpected token|doctype/i.test(
              msg
            ) ||
            e?.code === 'HTML_INSTEAD_OF_JSON'
          ) {
            setError('No tienes permiso para ver datos financieros en este dashboard.');
          } else {
            setError(msg);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canReadFinance]);

  const kpiCards = useMemo(() => {
    const totalIncome = Number(summary?.total_income ?? 0);
    const totalExpense = Number(summary?.total_expense ?? 0);
    const net = totalIncome - totalExpense;
    const totalBalance = Number(summary?.total_balance ?? 0);
    const netLabel = net >= 0 ? '▲ Resultado positivo' : '▼ Resultado negativo';
    return [
      {
        title: 'INGRESOS TOTALES',
        value: formatMoney(totalIncome),
        helper: 'Suma movimientos tipo ingreso',
        tone: 'positive',
      },
      {
        title: 'EGRESOS TOTALES',
        value: formatMoney(totalExpense),
        helper: 'Suma movimientos tipo egreso',
        tone: 'negative',
      },
      {
        title: 'UTILIDAD NETA (ING − EGR)',
        value: formatMoney(net),
        helper: netLabel,
        tone: net >= 0 ? 'positive' : 'negative',
      },
      {
        title: 'SALDO CUENTAS',
        value: formatMoney(totalBalance),
        helper: `${summary?.total_accounts ?? 0} cuenta(s) activa(s)`,
        tone: 'info',
      },
    ];
  }, [summary]);

  const monthlyBars = useMemo(() => {
    const keys = lastNMonthKeys(6);
    const incomeByMonth = {};
    const expenseByMonth = {};
    keys.forEach((k) => {
      incomeByMonth[k] = 0;
      expenseByMonth[k] = 0;
    });
    entries.forEach((e) => {
      const d = parseEntryDate(e.entry_date);
      if (!d) return;
      const k = monthKeyFromDate(d);
      if (!keys.includes(k)) return;
      const amt = Number(e.amount || 0);
      const t = String(e.entry_type || '').toLowerCase();
      if (t === 'income') incomeByMonth[k] = (incomeByMonth[k] || 0) + amt;
      else if (t === 'expense') expenseByMonth[k] = (expenseByMonth[k] || 0) + amt;
    });
    const rows = keys.map((k) => ({
      month: monthLabel(k),
      key: k,
      ingresos: incomeByMonth[k] || 0,
      egresos: expenseByMonth[k] || 0,
    }));
    const maxVal = Math.max(1, ...rows.flatMap((r) => [r.ingresos, r.egresos]));
    return rows.map((r) => ({
      ...r,
      ingPct: Math.round((r.ingresos / maxVal) * 100),
      egrPct: Math.round((r.egresos / maxVal) * 100),
    }));
  }, [entries]);

  const expenseByCategory = useMemo(() => {
    const acc = {};
    entries.forEach((e) => {
      if (String(e.entry_type || '').toLowerCase() !== 'expense') return;
      const cat = String(e.category || 'Sin categoría').trim() || 'Sin categoría';
      acc[cat] = (acc[cat] || 0) + Number(e.amount || 0);
    });
    const total = Object.values(acc).reduce((a, b) => a + b, 0);
    const sorted = Object.entries(acc)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
    return { rows: sorted, total };
  }, [entries]);

  const expenseRowsFormatted = useMemo(() => {
    const { rows, total } = expenseByCategory;
    if (!total) return [];
    return rows.map((row, i) => ({
      label: row.label,
      value: formatMoney(row.value),
      color: EXPENSE_PALETTE[i % EXPENSE_PALETTE.length],
      pct: Math.round((row.value / total) * 100),
    }));
  }, [expenseByCategory]);

  const conicGradient = useMemo(() => {
    const { rows, total } = expenseByCategory;
    if (!total || !rows.length) return null;
    let start = 0;
    const parts = rows.map((row, i) => {
      const pct = (row.value / total) * 100;
      const color = EXPENSE_PALETTE[i % EXPENSE_PALETTE.length];
      const end = start + pct;
      const seg = `${color} ${start}% ${end}%`;
      start = end;
      return seg;
    });
    return `conic-gradient(${parts.join(', ')})`;
  }, [expenseByCategory]);

  const recentTransactions = useMemo(() => {
    return entries.slice(0, 15).map((e) => {
      const isIncome = String(e.entry_type || '').toLowerCase() === 'income';
      return {
        id: e.id,
        date: String(e.entry_date || '').slice(0, 10),
        description: e.description || e.entry_code || '—',
        category: e.category || '—',
        type: isIncome ? 'ingreso' : 'egreso',
        amount: `${isIncome ? '+' : '-'}${formatMoney(e.amount)}`,
      };
    });
  }, [entries]);

  if (!canReadFinance) {
    return (
      <div>
        <Heading level={2} isDarkMode={isDarkMode}>
          Dashboard BackOffice
        </Heading>
        <div
          style={{
            marginTop: 14,
            maxWidth: 520,
            background: colors.panel,
            border: `1px solid ${colors.panelBorder}`,
            borderRadius: 12,
            padding: '18px 16px',
          }}
        >
          <p style={{ margin: 0, color: colors.title, fontWeight: 700, fontSize: 15 }}>
            Indicadores financieros no disponibles
          </p>
          <p style={{ margin: '8px 0 0', color: colors.muted, fontSize: 13, lineHeight: 1.45 }}>
            Este panel resume ingresos, egresos y saldos. Tu rol no incluye el permiso de lectura
            financiera. Usa el menú de Pinturas / Comercial u otras áreas a las que tengas acceso.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Heading level={2} isDarkMode={isDarkMode}>
        Dashboard BackOffice
      </Heading>

      {error ? (
        <p style={{ color: '#dc2626', fontSize: '13px', marginBottom: '12px' }}>{error}</p>
      ) : null}
      {loading ? (
        <p style={{ color: colors.muted, fontSize: '13px', marginBottom: '12px' }}>Cargando datos…</p>
      ) : null}

      <div
        style={{
          padding: '2px',
          display: 'grid',
          gap: '12px',
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '10px' }}>
          {kpiCards.map((card) => (
            <div
              key={card.title}
              style={{
                background: colors.panel,
                border: `1px solid ${colors.panelBorder}`,
                borderRadius: '12px',
                padding: '12px 14px',
              }}
            >
              <p style={{ margin: 0, fontSize: '11px', letterSpacing: '0.4px', color: colors.headerMuted, fontWeight: 700 }}>
                {card.title}
              </p>
              <p style={{ margin: '6px 0 0', color: kpiColor(card.tone), fontWeight: 800, fontSize: '28px', lineHeight: 1.1 }}>
                {card.value}
              </p>
              <p style={{ margin: '8px 0 0', color: colors.muted, fontSize: '12px' }}>{card.helper}</p>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div
            style={{
              background: colors.panel,
              border: `1px solid ${colors.panelBorder}`,
              borderRadius: '12px',
              padding: '14px',
            }}
          >
            <h3 style={{ margin: 0, color: colors.title, fontSize: '17px' }}>Ingresos vs Egresos (últimos 6 meses)</h3>
            <p style={{ margin: '6px 0 0', fontSize: '12px', color: colors.muted }}>
              Barras proporcionales al mayor monto del período mostrado.
            </p>
            <div style={{ marginTop: '18px', display: 'grid', gap: '10px' }}>
              {monthlyBars.map((row) => (
                <div key={row.key}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                    <div
                      style={{
                        height: '18px',
                        width: `${Math.max(4, row.ingPct)}%`,
                        maxWidth: '100%',
                        background: '#34d399',
                        borderRadius: '6px',
                      }}
                    />
                    <div
                      style={{
                        height: '18px',
                        width: `${Math.max(4, row.egrPct)}%`,
                        maxWidth: '100%',
                        background: '#fb7185',
                        borderRadius: '6px',
                      }}
                    />
                  </div>
                  <p style={{ margin: '6px 0 0', fontSize: '11px', color: colors.muted, textAlign: 'center' }}>{row.month}</p>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '12px', display: 'flex', gap: '16px', fontSize: '12px', color: colors.text }}>
              <span style={{ color: '#34d399' }}>● Ingresos</span>
              <span style={{ color: '#fb7185' }}>● Egresos</span>
            </div>
          </div>

          <div
            style={{
              background: colors.panel,
              border: `1px solid ${colors.panelBorder}`,
              borderRadius: '12px',
              padding: '14px',
              display: 'grid',
              gridTemplateColumns: '180px 1fr',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <div>
              <h3 style={{ margin: 0, color: colors.title, fontSize: '17px' }}>Distribución de egresos</h3>
              <p style={{ margin: '6px 0 0', fontSize: '12px', color: colors.muted }}>Por categoría (movimientos egreso)</p>
              <div
                style={{
                  width: '126px',
                  height: '126px',
                  marginTop: '20px',
                  borderRadius: '999px',
                  background: conicGradient || 'linear-gradient(135deg, #e2e8f0, #cbd5e1)',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <div
                  style={{
                    width: '62px',
                    height: '62px',
                    borderRadius: '999px',
                    background: colors.panel,
                    border: `1px solid ${colors.panelBorder}`,
                    display: 'grid',
                    placeItems: 'center',
                    color: colors.muted,
                    fontSize: '11px',
                    textAlign: 'center',
                    padding: '4px',
                  }}
                >
                  {expenseByCategory.total ? `${expenseRowsFormatted.length} cat.` : 'Sin datos'}
                </div>
              </div>
            </div>
            <div style={{ display: 'grid', gap: '8px', alignContent: 'center' }}>
              {expenseRowsFormatted.length === 0 ? (
                <span style={{ color: colors.muted, fontSize: '13px' }}>No hay egresos categorizados aún.</span>
              ) : (
                expenseRowsFormatted.map((row) => (
                  <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: colors.text, fontSize: '13px' }}>
                      <span style={{ color: row.color }}>●</span> {row.label}{' '}
                      <span style={{ color: colors.muted, fontSize: '11px' }}>({row.pct}%)</span>
                    </span>
                    <strong style={{ color: colors.title, fontSize: '13px' }}>{row.value}</strong>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div
          style={{
            background: colors.panel,
            border: `1px solid ${colors.panelBorder}`,
            borderRadius: '12px',
            padding: '14px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h3 style={{ margin: 0, color: colors.title, fontSize: '17px' }}>Últimas transacciones</h3>
            <span style={{ fontSize: '12px', color: colors.muted }}>{entries.length} movimiento(s) en total</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['FECHA', 'DESCRIPCIÓN', 'CATEGORÍA', 'TIPO', 'MONTO'].map((header) => (
                    <th
                      key={header}
                      style={{
                        color: colors.headerMuted,
                        fontSize: '11px',
                        textAlign: 'left',
                        fontWeight: 700,
                        borderBottom: `1px solid ${colors.panelBorder}`,
                        padding: '8px 10px',
                      }}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ color: colors.muted, fontSize: '13px', padding: '16px 10px' }}>
                      No hay movimientos registrados. Crea cuentas e ingresa asientos en Finanzas.
                    </td>
                  </tr>
                ) : (
                  recentTransactions.map((tx) => (
                    <tr key={tx.id}>
                      <td style={{ color: colors.text, fontSize: '13px', padding: '9px 10px' }}>{tx.date}</td>
                      <td style={{ color: colors.text, fontSize: '13px', padding: '9px 10px' }}>{tx.description}</td>
                      <td style={{ color: colors.muted, fontSize: '13px', padding: '9px 10px' }}>{tx.category}</td>
                      <td style={{ padding: '9px 10px' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            minWidth: '70px',
                            textAlign: 'center',
                            fontSize: '11px',
                            fontWeight: 700,
                            borderRadius: '999px',
                            padding: '3px 10px',
                            color: tx.type === 'ingreso' ? '#065f46' : '#881337',
                            background: tx.type === 'ingreso' ? '#d1fae5' : '#ffe4e6',
                          }}
                        >
                          {tx.type}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '9px 10px',
                          fontWeight: 700,
                          color: tx.type === 'ingreso' ? '#34d399' : '#fb7185',
                          fontSize: '14px',
                        }}
                      >
                        {tx.amount}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackOfficeDashboardWorkspace;
